/* eslint-disable max-len */
/* eslint-disable react/destructuring-assignment */
// Authored by Colin Reitman
/** Added functionality by Kristy Street (added additional custom style options and ability to pass
  in icon container in data for options, onBlur for formik fields
) */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Select, { components, Creatable, createFilter } from 'react-select';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import IcomoonReact from 'icomoon-react';

import iconSet from '../../images/teambuildr-selection.json';
import Wrapper from './Wrapper';
import SelectTitle from './SelectTitle';

/** react-select's default control fill is the literal white hsl(0, 0%, 100%); replace it with
 * the input surface so a dark theme is honoured, but keep any explicit override. */
const themedControlBackground = (baseControl, colors) => (
  (baseControl.backgroundColor && baseControl.backgroundColor !== 'hsl(0, 0%, 100%)')
    ? baseControl.backgroundColor
    : (colors.surfaceInput || baseControl.backgroundColor)
);

const ErrorText = styled('div')`
  color: #CC0404;
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 700;
  margin-top: 5px;
`;

const IconContainer = styled('div')`
  height: 24px;
  width: 24px;
  border-radius: ${(props) => (props.square ? '4px' : '50%')};
  color: ${(props) => (props.color ? props.color : '#444444')};
  border: ${(props) => (props.square ? 'none' : 'solid 1px')};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: ${(props) => (props.square ? '8px' : '5px')};
  ${(props) => props.backgroundColor && `background: ${props.backgroundColor}`};
  ${(props) => props.square && 'padding: 4px'}; 
`;

const CustomOption = (props) => (
  <components.Option {...props}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: props.data.includeCheckmark ? 'space-between' : '' }}>
      <div style={{ display: 'flex' }}>
        {props.data.avatar && !props.data.avatarRight && (
          <div style={{
            marginRight: 5,
          }}
          >
            {props.data.avatar}
          </div>
        )}
        {!props.data.avatar && (
          props?.data?.includeIconContainer ? (
            <IconContainer color={props.data.iconContainerColor} square={props.data?.squareContainer} backgroundColor={props.data?.backgroundColor}>
              <IcomoonReact
                iconSet={iconSet}
                size={16}
                icon={props.data.icon}
                color={props.data.color || '#444444'}
              />
            </IconContainer>
          ) : (
            <IcomoonReact
              iconSet={iconSet}
              size={16}
              icon={props.data.icon}
              color={props.data.color || '#444444'}
              style={{ marginRight: 5 }}
            />
          )
        )}
        {props.children}
        {props.data.avatar && props.data.avatarRight && (
          <div style={{
            marginLeft: 5,
            alignSelf: 'center',
          }}
          >
            {props.data.avatar}
          </div>
        )}
      </div>
      {props.data.includeCheckmark && (
      <IcomoonReact
        iconSet={iconSet}
        size={12}
        icon='checkmark'
        color={props.data.checkmarkColor || '#444444'}
        style={{ marginRight: 5 }}
      />
      )}
    </div>
  </components.Option>
);

const ValueIcon = (props) => {
  if (props.data?.includeIconContainer) {
    return (
      <IconContainer color={props.data.iconContainerColor} square={props.data?.squareContainer} backgroundColor={props.data?.backgroundColor}>
        <IcomoonReact
          iconSet={iconSet}
          size={16}
          icon={props.data.icon}
          color={props.data.color || '#444444'}
        />
      </IconContainer>
    );
  }
  return (
    <IcomoonReact
      iconSet={iconSet}
      size={16}
      icon={props.data.icon}
      color={props.data.color || '#444444'}
      style={{ marginRight: 5 }}
    />
  );
};

const CustomSingleValue = (props) => (
  <components.SingleValue {...props}>
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {!props.data.avatar ? (
        <ValueIcon data={props.data} />
      ) : (
        <div style={{
          marginRight: 5,
        }}
        >
          {props.data.avatar}
        </div>
      )}
      {props.children}
    </div>
  </components.SingleValue>
);

const CustomMultiValue = (props) => (
  <components.MultiValue {...props}>
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {!props.data.avatar ? (
        <ValueIcon data={props.data} />
      ) : (
        <div style={{
          marginRight: 5,
        }}
        >
          {props.data.avatar}
        </div>
      )}
      {props.children}
    </div>
  </components.MultiValue>
);

/**
 * @param {Object} defaultValue default selected value for the select box,
 * @param {Object} value value for the select box,
 * passed in as an index of the options array
 * @param {String} height the height of the wrapper containing the select box. Can be passed in
 * as a fixed value (e.g. '200px') or as a percentage (e.g. '50%')
 * @param {Bool} isMulti determines whether or not select box can select multiple items
 * @param {Bool} innerContainerWidth Determines width of inner container, defaults to 100%
 * @param {Bool} isSearchable determines whether or not component can accept a search string
 * @param {Func} onChangeFunction function that is run when an option is selected. Will
 * be called passing in the selected option
 * @param {Array} options the array of selectable options, with each option taking the shape of:
 * { name: 'option name', id: 1 }
 * @param {Bool} closeMenuOnSelect adding false causes select menu to persist after select
 * selecting a value
 * * @param {Bool} closeMenuOnResize close menu when window is resizing
 * @param {String} placeholder adds a pleceholder to select box
 * @param {Bool} removeSeparator boolean to remove the IndicatorSeperator from Select
 * @param {Object} styledProps accepts an object with all optional styles
 * @param {String} width the width of the wrapper containing the select box. Can be passed in
 * * as a fixed value (e.g. '200px') or as a percentage (e.g. '50%')
 * @param {Bool} error bool to decide whether or not to display error messages and styling
 * @param {String} errorMsg custom message to display below the select input when there is an error
 * @param {Function} onBlur can be used to handle blur events for formik (ie touched)
 * @param {Bool} includeIconContainer boolean value that you can pass if you are
    choosing to use icons in your dropdown or your inputs and want that icon circle
    container around it
 * @param {Bool} isClearable if you do not wish to render the clearIndicator inside the mutliselect
    set isClearable to false
 * @param {Bool} isCreateable will transform select into a <Creatable/> select where you can type to add new options
 * @param {Func} onCreateOption function that you can/should pass with a creatable, that would allow you set values or to do something onCreate...for
    expample if you need to make an async funtion call to an api to create the option on your back end and then add that to the toptions list
 * @param {Object} value value to pass the select box, if don't pass value react select will handle it with default behavior
 * @param {elementType} menuListComponent if you would like to pass a custom menuList (for example if you want to pass a windowed list)
 * @param {Element} menuPortalTarget pass document.body to portal the dropdown menu outside the current stacking context — use this when the select is inside a modal or overflow:hidden container where the menu would otherwise be clipped
 * @param {Function} onMenuOpen callback fired when the dropdown menu opens
 * @param {Function} onMenuClose callback fired when the dropdown menu closes
 * @param {Object} customStyles option to pass in custom styles in the shape of...
 * customStyles={{
    control: (provided, state) => ({
      ...provided,
      fontFamily: 'Nunito Sans',
      fontSize: '16px',
      fontWeight: 400,
      borderColor: state.isFocused ? 'lightgray' : 'lightgray',
      boxShadow: state.isFocused ? 'none' : 'none',
        &:hover': {
        borderColor: state.isFocused ? 'lightgray' : 'lightgray',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      borderRadius: '8px',
      backgroundColor: state.isFocused ? 'rgba(207, 245, 232, 1)' : 'white',
      color: '#444444',
      fontFamily: 'Nunito Sans',
      fontSize: '16px',
      fontWeight: 700,
      '&:hover': {
        backgroundColor: 'rgba(207, 245, 232, 1)',
      },
    }),
    menu: (provided) => ({
      ...provided,
      border: '0px',
      maxWidth: '260px',
      minWidth: '250px',
      padding: '16px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9E9E9E', // Custom placeholder color
    }),
  }}
 * @returns
 */
const SelectBox = ({
  // defaultValue = {},
  defaultValue,
  height = '100%',
  isMulti,
  innerContainerWidth = '100%',
  isSearchable = true,
  onChangeFunction,
  onInputChange,
  onCreateOption,
  options,
  closeMenuOnSelect = true,
  placeholder = '',
  removeSeparator,
  styledProps = {},
  title = '',
  titleStyledProps = {},
  width = '100%',
  customStyles = {},
  error,
  errorMsg,
  onBlur,
  isClearable,
  menuListComponent,
  isDisabled,
  value,
  closeOnResize,
  isCreateable,
  isLoading,
  formatOptionLabel,
  noOptionsMessage,
  menuPortalTarget,
  onMenuOpen,
  onMenuClose,
}) => {
  const { colors, isDark } = useTheme();
  const selectRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (selectRef.current) {
        selectRef.current.blur(); /** Close the dropdown */
      }
    };

    if (closeOnResize) {
      window.addEventListener('resize', handleResize);
    }

    /** Return cleanup function to remove the event listener */
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [closeOnResize]);

  return (
    <Wrapper
      width={width}
      height={height}
      {...styledProps}
    >
      {title ? (
        <SelectTitle {...titleStyledProps} style={{ color: isDisabled && '#BDBDBD' }}>
          {title}
        </SelectTitle>
      ) : null}
      {isCreateable ? (
        <Creatable
             /** this helps with performance when searching, since we don't have accents in english */
          filterOption={createFilter({ ignoreAccents: false })}
          ref={selectRef}
          isValidNewOption={(inputValue) => {
            /** Only allow "create value" if input doesn't match any existing option */
            const isDuplicate = options.some(
              (option) => option.name.toLowerCase() === inputValue.toLowerCase(),
            );
            return !isDuplicate && inputValue.trim().length > 0;
          }}
          isClearable={isClearable}
          onBlur={onBlur}
          formatCreateLabel={(inputValue) => `Add ${inputValue}...`}
          components={{
            Option: CustomOption,
            SingleValue: CustomSingleValue,
            MultiValue: CustomMultiValue,
            MenuList: menuListComponent || components.MenuList,
          }}
          value={value}
          // defaultValue={Object?.keys(defaultValue)?.length ? defaultValue : null}
          defaultValue={defaultValue || null}
          getOptionLabel={(option) => option.name || option.label}
          getOptionValue={(option) => option.id || option.value}
          isDisabled={isDisabled}
          isMulti={isMulti}
          isSearchable={isSearchable}
          isLoading={isLoading}
          onChange={(option) => {
            onChangeFunction(option);
          }}
          onInputChange={(changeValue) => onInputChange(changeValue)}
          onCreateOption={(option) => {
            onCreateOption(option);
          }}
          useAvatars
          blurInputOnSelect={closeMenuOnSelect}
          closeMenuOnSelect={closeMenuOnSelect}
          formatOptionLabel={formatOptionLabel}
          noOptionsMessage={() => noOptionsMessage}
          options={options}
          placeholder={placeholder}
          onMenuOpen={onMenuOpen}
          onMenuClose={onMenuClose}
          /** components can be custom styled using the styles api
           * defined in the react-select docs:
           * https://react-select.com/components#replacing-components
          */
          styles={{
            ...customStyles,
            container: (provided) => ({
              ...provided,
              width: innerContainerWidth,
            }),
            /** adding styles for control if disabled */
            control: (provided, state) => {
              const baseControl = customStyles.control
                ? customStyles.control(provided, state)
                : provided;

              return {
                ...baseControl,
                backgroundColor: isDisabled ? colors.surface : themedControlBackground(baseControl, colors),
                borderColor: isDisabled ? colors.borderLight : baseControl.borderColor,
                opacity: isDisabled ? 1 : baseControl.opacity, /** remove greyed-out default opacity */
              };
            },
            noOptionsMessage: (provided) => ({
              ...provided,
              fontSize: '16px',
              fontFamily: 'nunito sans',
            }),
            placeholder: (provided, state) => {
              const base = customStyles.placeholder ? customStyles.placeholder(provided, state) : provided;
              return {
                ...base,
                // Only dark needs a hand: react-select's own placeholder grey clears AA on white.
                color: (base.color && base.color !== 'hsl(0, 0%, 50%)') ? base.color : (isDark ? colors.textSecondary : base.color),
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              };
            },
            singleValue: (provided, state) => {
              const base = customStyles.singleValue ? customStyles.singleValue(provided, state) : provided;
              return {
                ...base,
                color: (base.color && base.color !== 'hsl(0, 0%, 20%)') ? base.color : (isDark ? colors.text : base.color),
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              };
            },
          }}
        />
      ) : (
        <Select
          /** this helps with performance when searching, since we don't have accents in english */
          filterOption={createFilter({ ignoreAccents: false })}
          ref={selectRef}
          isClearable={isClearable}
          onBlur={onBlur}
          components={{
            Option: CustomOption,
            SingleValue: CustomSingleValue,
            MultiValue: CustomMultiValue,
            MenuList: menuListComponent || components.MenuList,
          }}
          value={value}
          // defaultValue={Object?.keys(defaultValue)?.length ? defaultValue : null}
          defaultValue={defaultValue || null}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
          isDisabled={isDisabled}
          isMulti={isMulti}
          isSearchable={isSearchable}
          onChange={(option) => {
            onChangeFunction(option);
          }}
          useAvatars
          blurInputOnSelect={closeMenuOnSelect}
          closeMenuOnSelect={closeMenuOnSelect}
          formatOptionLabel={formatOptionLabel}
          options={options}
          placeholder={placeholder}
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPortalTarget ? 'fixed' : undefined}
          onMenuOpen={onMenuOpen}
          onMenuClose={onMenuClose}
        /** components can be custom styled using the styles api
         * defined in the react-select docs:
         * https://react-select.com/components#replacing-components
        */
          styles={{
            ...customStyles,
            menuPortal: (base) => ({
              ...(customStyles.menuPortal ? customStyles.menuPortal(base) : base),
              zIndex: 9999,
            }),
            container: (provided) => ({
              ...provided,
              width: innerContainerWidth,
            }),
            /** adding styles for control if disabled */
            control: (provided, state) => {
              const baseControl = customStyles.control
                ? customStyles.control(provided, state)
                : provided;

              return {
                ...baseControl,
                backgroundColor: isDisabled ? colors.surface : themedControlBackground(baseControl, colors),
                borderColor: isDisabled ? colors.borderLight : baseControl.borderColor,
                opacity: isDisabled ? 1 : baseControl.opacity, /** remove greyed-out default opacity */
              };
            },
            indicatorSeparator: (base) => ({
              ...base,
              display: removeSeparator ? 'none' : 'black',
            }),
            placeholder: (provided, state) => {
              const base = customStyles.placeholder ? customStyles.placeholder(provided, state) : provided;
              return {
                ...base,
                // Only dark needs a hand: react-select's own placeholder grey clears AA on white.
                color: (base.color && base.color !== 'hsl(0, 0%, 50%)') ? base.color : (isDark ? colors.textSecondary : base.color),
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              };
            },
            singleValue: (provided, state) => {
              const base = customStyles.singleValue ? customStyles.singleValue(provided, state) : provided;
              return {
                ...base,
                color: (base.color && base.color !== 'hsl(0, 0%, 20%)') ? base.color : (isDark ? colors.text : base.color),
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              };
            },
          }}
        />
      )}
      {error && (
      <ErrorText>{errorMsg}</ErrorText>
      )}
    </Wrapper>
  );
};

//
SelectBox.propTypes = {
  defaultValue: PropTypes.instanceOf(Object),
  value: PropTypes.instanceOf(Object),
  height: PropTypes.string,
  innerContainerWidth: PropTypes.string,
  isMulti: PropTypes.bool,
  isSearchable: PropTypes.bool,
  onChangeFunction: PropTypes.func.isRequired,
  onCreateOption: PropTypes.func,
  onInputChange: PropTypes.func,
  options: PropTypes.instanceOf(Array).isRequired,
  closeMenuOnSelect: PropTypes.bool,
  closeOnResize: PropTypes.bool,
  placeholder: PropTypes.string,
  removeSeparator: PropTypes.bool,
  styledProps: PropTypes.instanceOf(Object),
  title: PropTypes.string,
  titleStyledProps: PropTypes.instanceOf(Object),
  width: PropTypes.string,
  customStyles: PropTypes.instanceOf(Object),
  error: PropTypes.bool,
  errorMsg: PropTypes.string,
  onBlur: PropTypes.func,
  isClearable: PropTypes.bool,
  menuListComponent: PropTypes.elementType,
  isCreateable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  formatOptionLabel: PropTypes.func,
  noOptionsMessage: PropTypes.string,
  isLoading: PropTypes.bool,
  menuPortalTarget: PropTypes.instanceOf(Element),
  onMenuOpen: PropTypes.func,
  onMenuClose: PropTypes.func,
};

SelectBox.defaultProps = {
  // defaultValue: {},
  defaultValue: undefined,
  height: '100%',
  innerContainerWidth: '100%',
  isSearchable: true,
  isMulti: false,
  styledProps: {},
  width: '100%',
  closeMenuOnSelect: true,
  closeOnResize: false,
  placeholder: '',
  removeSeparator: false,
  title: '',
  titleStyledProps: {},
  customStyles: {},
  error: false,
  errorMsg: '',
  onBlur: () => {},
  onCreateOption: undefined,
  onInputChange: () => {},
  isClearable: true,
  menuListComponent: null,
  value: undefined,
  isCreateable: false,
  formatOptionLabel: undefined,
  noOptionsMessage: 'No Options',
  isDisabled: false,
  isLoading: false,
  menuPortalTarget: undefined,
  onMenuOpen: undefined,
  onMenuClose: undefined,
};

ValueIcon.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
};

CustomOption.propTypes = {
  children: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.instanceOf(Object).isRequired,
  props: PropTypes.instanceOf(Object).isRequired,
};

CustomSingleValue.propTypes = {
  children: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.instanceOf(Object).isRequired,
  props: PropTypes.instanceOf(Object).isRequired,
};

CustomMultiValue.propTypes = {
  children: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.instanceOf(Object).isRequired,
  props: PropTypes.instanceOf(Object).isRequired,
};

export default SelectBox;
